import { createTelemetry, type Telemetry, type TelemetryEvent } from './telemetry.js'

/**
 * The app's one product-telemetry instance and the person's saved choice (§16.3, BRD-04).
 *
 * The choice lives in the device's vault, so it survives a reload and the installed apps share
 * the code. Nothing is tracked until the stored choice has been read: an event raised during
 * start-up can never slip out ahead of an opt-out.
 */

export const TELEMETRY_SETTING_KEY = 'telemetry.enabled'

/**
 * BRD-04 makes product telemetry optional ("User disables optional product telemetry"); the PRD
 * does not state the default, so it stays as the app already behaved: on until turned off.
 */
export const TELEMETRY_DEFAULT = true

export interface TelemetryPreferenceStore {
  getSetting<T = unknown>(key: string): Promise<T | null>
  putSetting(key: string, value: unknown): Promise<unknown>
}

export interface ProductTelemetry {
  readonly telemetry: Telemetry
  /**
   * Reads the saved choice (the default when none is saved) and starts honouring it. A choice made
   * while the read was in flight wins; the answer is always the choice now in force.
   */
  load(store: TelemetryPreferenceStore | null): Promise<boolean>
  /** Applies a new choice at once and saves it. Without a vault it lasts until the page closes. */
  choose(store: TelemetryPreferenceStore | null, enabled: boolean): Promise<void>
}

export function createProductTelemetry(sink: (event: TelemetryEvent) => void): ProductTelemetry {
  const telemetry = createTelemetry({ enabled: false, sink })
  // Counts choices, so a read that started before one cannot overwrite it when it lands.
  let choices = 0
  return {
    telemetry,
    async load(store) {
      const before = choices
      const stored = store === null ? null : await store.getSetting<unknown>(TELEMETRY_SETTING_KEY)
      if (choices !== before) return telemetry.enabled()
      const enabled = typeof stored === 'boolean' ? stored : TELEMETRY_DEFAULT
      telemetry.setEnabled(enabled)
      return enabled
    },
    async choose(store, enabled) {
      choices += 1
      telemetry.setEnabled(enabled)
      if (store !== null) await store.putSetting(TELEMETRY_SETTING_KEY, enabled)
    },
  }
}

/**
 * HACK: the sink is a no-op. No telemetry endpoint or event call exists yet, so nothing leaves the
 * device; wiring a real sink here is all it takes, and the saved choice already gates it.
 */
export const productTelemetry = createProductTelemetry(() => {})
